/**
 * Authentication store.
 *
 * Holds the Supabase session and exposes the derived state the router guards and
 * the app shell need. The store is the single place that talks to
 * `supabase.auth`, so sign-in/sign-out behaviour cannot drift between views.
 */
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Session, User } from '@supabase/supabase-js';
import { isSupabaseConfigured, supabase } from '@/lib/supabase';
import { api } from '@/lib/api';
import { i18n } from '@/i18n';

export const useAuthStore = defineStore('auth', () => {
  const session = ref<Session | null>(null);
  const user = ref<User | null>(null);
  /**
   * True until the initial session lookup finishes. Router guards must wait for
   * this, otherwise a page refresh briefly looks unauthenticated and bounces the
   * user to the login screen.
   */
  const initialising = ref(true);
  const signingIn = ref(false);
  const errorMessage = ref<string | null>(null);

  const isAuthenticated = computed(() => session.value !== null);
  const email = computed(() => user.value?.email ?? null);

  /**
   * App-level super admin (backend SUPER_ADMIN_EMAILS). Purely a UI affordance:
   * it decides whether the batch-swap nav entry renders. The backend re-checks
   * on every admin call, so a spoofed flag here yields only 403s.
   */
  const superAdmin = ref(false);
  const superAdminChecked = ref(false);

  /** Asks the backend once per session whether this account is a super admin. */
  async function refreshSuperAdmin(): Promise<boolean> {
    if (!isAuthenticated.value) {
      superAdmin.value = false;
      superAdminChecked.value = true;
      return false;
    }
    try {
      const res = await api.get<{ superAdmin: boolean }>('/api/admin/me');
      superAdmin.value = res.superAdmin;
    } catch {
      // Backend unreachable or older version: behave as a normal user.
      superAdmin.value = false;
    }
    superAdminChecked.value = true;
    return superAdmin.value;
  }

  /** Reads any persisted session and subscribes to future auth changes. */
  async function initialise(): Promise<void> {
    if (!isSupabaseConfigured) {
      initialising.value = false;
      return;
    }
    try {
      const { data } = await supabase.auth.getSession();
      session.value = data.session;
      user.value = data.session?.user ?? null;

      // Keeps the store in step with token refreshes and programmatic
      // sign-outs. (Sessions are per-tab now — sessionStorage — so there are
      // no cross-tab auth events to react to.)
      supabase.auth.onAuthStateChange((_event, next) => {
        session.value = next;
        user.value = next?.user ?? null;
      });
    } finally {
      initialising.value = false;
    }
  }

  /**
   * Signs in with email + password.
   * Returns true on success; the caller navigates. Errors are surfaced inline
   * via `errorMessage` rather than thrown, because the login form renders them.
   */
  async function signIn(emailValue: string, password: string): Promise<boolean> {
    errorMessage.value = null;

    if (!isSupabaseConfigured) {
      errorMessage.value = i18n.global.t('auth.notConfigured');
      return false;
    }

    signingIn.value = true;
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: emailValue.trim(),
        password,
      });
      if (error) {
        // Supabase returns "Invalid login credentials" for both a wrong password
        // and an unknown address, which is the correct behaviour — it prevents
        // account enumeration. Other server messages are surfaced as-is.
        errorMessage.value =
          error.message === 'Invalid login credentials'
            ? i18n.global.t('auth.invalidCredentials')
            : error.message;
        return false;
      }
      session.value = data.session;
      user.value = data.user;
      return true;
    } catch (err) {
      errorMessage.value = (err as Error).message || i18n.global.t('auth.signInFailed');
      return false;
    } finally {
      signingIn.value = false;
    }
  }

  async function signOut(): Promise<void> {
    if (isSupabaseConfigured) {
      await supabase.auth.signOut().catch(() => undefined);
    }
    session.value = null;
    user.value = null;
    superAdmin.value = false;
    superAdminChecked.value = false;
    // The record selection persists across reloads (sessionStorage), so an
    // explicit sign-out must wipe it — this tab may be reused by the next
    // person without ever being closed.
    const { useSelectionStore } = await import('./selection');
    useSelectionStore().clear();
  }

  return {
    session,
    user,
    initialising,
    signingIn,
    errorMessage,
    isAuthenticated,
    email,
    superAdmin,
    superAdminChecked,
    refreshSuperAdmin,
    initialise,
    signIn,
    signOut,
  };
});
